import * as Sentry from '@sentry/react';
import { injectable } from 'inversify';

import {
  DEFAULT_CONFIGURATION,
  type IMonitoringService,
  type MonitoringBreadcrumb,
  MonitoringConfiguration,
  type MonitoringUserContext,
} from './monitoring.types';

/**
 * Monitoring service for error tracking and performance monitoring
 * This is a domain-agnostic service that can be used by any module
 * Implementation uses Sentry under the hood
 */
@injectable()
export class MonitoringService implements IMonitoringService {
  private static instance: MonitoringService;

  /**
   * Get singleton instance of MonitoringService
   * @returns MonitoringService instance
   */
  public static getInstance(): MonitoringService {
    if (!MonitoringService.instance) {
      MonitoringService.instance = new MonitoringService();
    }

    return MonitoringService.instance;
  }

  /**
   * Initialize monitoring service with configuration from environment
   */
  public initialize(config: Partial<MonitoringConfiguration>): void {
    try {
      const environment = config?.environment ?? DEFAULT_CONFIGURATION.environment;

      Sentry.init({
        dsn: config.dsn,
        maxBreadcrumbs: config.maxBreadcrumbs ?? DEFAULT_CONFIGURATION.maxBreadcrumbs,
        release: config?.release ?? DEFAULT_CONFIGURATION.release,
        integrations: [
          Sentry.captureConsoleIntegration({
            levels: config.levels ?? DEFAULT_CONFIGURATION.levels,
          }),
          Sentry.browserTracingIntegration(),
          Sentry.breadcrumbsIntegration({
            ...DEFAULT_CONFIGURATION.breadcrumbs,
            ...config.breadcrumbs,
          }),
          Sentry.replayIntegration({
            ...DEFAULT_CONFIGURATION.replay,
            ...config.replay,
          }),
        ],
        environment,
        tracesSampleRate: Number(config?.tracesSampleRate ?? 1),
        attachStacktrace: true,
        sampleRate: Number(config?.sampleRate ?? 1),
        replaysSessionSampleRate: Number(
          config?.replaysSessionSampleRate ?? (environment === 'prod' ? 0.01 : 0.1)
        ),
        replaysOnErrorSampleRate: Number(
          config?.replaysOnErrorSampleRate ?? (environment === 'prod' ? 0.1 : 1)
        ),

        beforeSendTransaction(transaction) {
          // Normalize URLs by removing trailing slashes for consistent reporting
          if (transaction.contexts?.trace?.data) {
            const traceData = transaction.contexts.trace.data;

            if (traceData.url && typeof traceData.url === 'string') {
              traceData.url = traceData.url.replace(/\/$/, '');
            }

            if (traceData['http.url'] && typeof traceData['http.url'] === 'string') {
              traceData['http.url'] = traceData['http.url'].replace(/\/$/, '');
            }
          }

          // Normalize transaction name if it contains a URL path
          if (transaction.transaction && transaction.transaction.endsWith('/')) {
            transaction.transaction = transaction.transaction.replace(/\/$/, '');
          }

          // Enhance transaction name with project type if available
          const projectType = transaction.tags?.['project.type'];

          if (
            projectType &&
            typeof projectType === 'string' &&
            transaction.transaction &&
            typeof transaction.transaction === 'string'
          ) {
            // Append project type to transaction name for better filtering
            // Example: "GET /editor" becomes "GET /editor [template]"
            if (!transaction.transaction.includes(`[${projectType}]`)) {
              transaction.transaction = `${transaction.transaction} [${projectType}]`;
            }
          }

          return transaction;
        },
      });
    } catch (error) {
      console.error('Sentry initialization failed:', error);
    }
  }

  /**
   * Set user context for error tracking
   * @param userContext User information
   */
  public setUser(userContext: MonitoringUserContext): void {
    try {
      Sentry.setUser({
        id: userContext.userId,
        email: userContext.email,
        workspace: userContext.workspaceDomain,
      });
    } catch (error) {
      console.error('Failed to set monitoring user context:', error);
    }
  }

  /**
   * Clear user context
   */
  public clearUser(): void {
    try {
      Sentry.setUser(null);
    } catch (error) {
      console.error('Failed to clear monitoring user context:', error);
    }
  }

  public setProjectContext(projectType: 'design' | 'template', projectId?: string) {
    try {
      Sentry.setTag('project.type', projectType);

      if (projectId) {
        Sentry.setTag('project.id', projectId);
      }

      // Set context for better grouping in Sentry
      Sentry.setContext('project', {
        type: projectType,
        id: projectId,
      });
    } catch (error) {
      console.error('Failed to set Sentry project context:', error);
    }
  }

  /**
   * Log exception to monitoring service with optional tags
   * @param error Error to log
   * @param tags Optional tags to add to this specific error
   */
  public logException(error: Error, tags?: Record<string, string | number | boolean>): void {
    try {
      if (tags) {
        Sentry.setTags(tags);
      }

      const trimmedError = this.trimErrorData(error);

      Sentry.captureException(trimmedError);
    } catch (monitoringError) {
      console.error('Failed to log exception to monitoring service:', monitoringError);
    }
  }

  /**
   * Capture an exception with additional context
   * @param error Error to capture
   * @param contextName Name of the context (e.g., 'payment', 'editor', 'upload')
   * @param contextData Context data specific to the domain
   * @param tags Optional tags for filtering
   */
  public captureException(
    error: Error,
    context?: string,
    contextData?: Record<string, any>,
    tags?: Record<string, string | number | boolean>
  ): void {
    try {
      if (tags) {
        Sentry.setTags(tags);
      }

      if (context && contextData) {
        Sentry.setContext(context, contextData);
      }

      const trimmedError = this.trimErrorData(error);

      Sentry.captureException(trimmedError);
    } catch (monitoringError) {
      console.error('Failed to capture exception to monitoring service:', monitoringError);
    }
  }

  /**
   * Set a named context for subsequent operations
   * @param contextName Name of the context (e.g., 'payment', 'editor', 'upload')
   * @param contextData Context data
   */
  public setContext(contextName: string, contextData: Record<string, any> | null): void {
    try {
      Sentry.setContext(contextName, contextData);
    } catch (error) {
      console.error(`Failed to set context '${contextName}':`, error);
    }
  }

  /**
   * Set tags for filtering and grouping errors
   * @param tags Key-value pairs of tags
   */
  public setTags(tags: Record<string, string | number | boolean | undefined>): void {
    try {
      Sentry.setTags(tags);
    } catch (error) {
      console.error('Failed to set tags:', error);
    }
  }

  /**
   * Add a breadcrumb for tracking user actions
   * @param breadcrumb Breadcrumb data
   */
  public addBreadcrumb(breadcrumb: MonitoringBreadcrumb): void {
    try {
      Sentry.addBreadcrumb({
        category: breadcrumb.category,
        message: breadcrumb.message,
        level: breadcrumb.level || 'info',
        data: breadcrumb.data,
      });
    } catch (error) {
      console.error('Failed to add breadcrumb:', error);
    }
  }

  /**
   * Start a transaction for performance tracking
   * @param name Transaction name
   * @param op Operation type (e.g., 'http', 'db', 'payment', 'upload')
   * @param description Optional description
   * @returns Sentry transaction or undefined if Sentry is not initialized
   */
  public startTransaction(
    name: string,
    op: string,
    description?: string
  ): Sentry.Transaction | undefined {
    try {
      return Sentry.startTransaction({
        name,
        op,
        description: description || name,
      });
    } catch (error) {
      console.error('Failed to start transaction:', error);
      return undefined;
    }
  }

  /**
   * Trim error data to avoid sending oversized data to Sentry
   * https://develop.sentry.dev/sdk/envelopes/#size-limits
   * @param error Error to trim
   * @returns Trimmed error
   */
  private trimErrorData(error: Error): Error {
    const maxStringLength = 700;

    try {
      const trimmedError = new Error(error.message);

      trimmedError.name = error.name;

      if (error.stack && error.stack.length > maxStringLength) {
        trimmedError.stack = error.stack.substring(0, maxStringLength) + '...';
      } else {
        trimmedError.stack = error.stack;
      }

      Object.entries(error).forEach(([key, value]) => {
        if (!(key in trimmedError)) {
          if (typeof value === 'string' && value.length > maxStringLength) {
            (trimmedError as any)[key] = value.substring(0, maxStringLength) + '...';
          } else {
            (trimmedError as any)[key] = value;
          }
        }
      });

      return trimmedError;
    } catch (trimError) {
      console.error('Error while trimming the error object:', trimError);

      return error;
    }
  }
}
